/**
 * LiveClassesCalendar — monthly calendar view + tabular list view for live classes.
 *
 * - Header: month prev/next + Today, host filter (All/Only me), view toggle (Cal/List).
 * - Calendar grid: 6-row × 7-col month, class chips coloured by status.
 * - List view: chronological table (read-only; CRUD lives on Scheduled Classes tab).
 * - Row / chip click → LiveClassDetailsDrawer with Start Class action.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ChevronLeft, ChevronRight, CalendarDays, List, Loader2, Radio, Users, Calendar } from 'lucide-react';
import LiveClassDetailsDrawer from './LiveClassDetailsDrawer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---------- calendar math helpers ----------
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];

const GRADE_OPTIONS = [
  'MEO Class 1', 'MEO Class 2', 'MEO Class 4', 'ETO', 'Master', 'Chief Mate', '2MFG',
];

// Get local (browser) calendar for a class in IST.
// We treat class.start_time as-is (already IST-tagged); browsers will render
// its date components in the local zone. Since our users are all IST, this is fine.
const toIST = (iso) => {
  try {
    const d = new Date(iso);
    // Represent as IST components
    const s = d.toLocaleString('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
    // e.g. "2026-02-14, 10:00"
    const [datePart, timePart] = s.split(', ');
    const [y, mo, da] = datePart.split('-').map(Number);
    const [h, mi] = timePart.split(':').map(Number);
    return { year: y, month: mo - 1, date: da, hour: h, minute: mi, iso };
  } catch { return null; }
};

const buildMonthGrid = (year, month) => {
  // Returns 6-row × 7-col grid of Date objects (some in prev/next month for filler)
  const firstOfMonth = new Date(year, month, 1);
  const firstDayCol = firstOfMonth.getDay(); // 0..6 Sun..Sat
  const gridStart = new Date(year, month, 1 - firstDayCol);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }
  return cells;
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const isSameISTDay = (istParts, dateObj) =>
  istParts &&
  istParts.year === dateObj.getFullYear() &&
  istParts.month === dateObj.getMonth() &&
  istParts.date === dateObj.getDate();

const chipStyle = (status) => {
  switch (status) {
    case 'live':      return 'bg-red-500 text-white animate-pulse hover:bg-red-600';
    case 'scheduled': return 'bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-200';
    case 'completed': return 'bg-gray-200 text-gray-600 hover:bg-gray-300';
    case 'cancelled': return 'bg-red-50 text-red-500 line-through hover:bg-red-100';
    default:          return 'bg-gray-100 text-gray-700 hover:bg-gray-200';
  }
};

const fmtISTLong = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

// ---------- component ----------

const LiveClassesCalendar = () => {
  const { user } = useAuth();
  const isFullAdmin = user?.role === 'admin';

  // Sub-admin defaults to "Only Me", full admin defaults to "All"
  const [hostFilter, setHostFilter] = useState(isFullAdmin ? 'all' : 'mine');
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' | 'list'
  const [showCompleted, setShowCompleted] = useState(false);
  const [gradeFilter, setGradeFilter] = useState('all');

  const [monthCursor, setMonthCursor] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerClass, setDrawerClass] = useState(null);

  // Pagination for the List view
  const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(10);
  // Reset to first page whenever the filter set that feeds the list changes
  useEffect(() => { setListPage(1); }, [hostFilter, gradeFilter, viewMode]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const scope = hostFilter === 'mine' ? 'mine' : 'all';
      const { data } = await axios.get(`${API}/admin/live-classes`, { params: { scope } });
      setClasses(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load classes');
    } finally {
      setLoading(false);
    }
  }, [hostFilter]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 60s so LIVE status stays fresh
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  // Precompute IST parts once per class list
  const classesWithIST = useMemo(
    () => classes.map(c => ({ ...c, _ist: toIST(c.start_time) })),
    [classes]
  );

  // Filter out completed unless the toggle is on, and by grade
  const visibleClasses = useMemo(() => {
    let list = classesWithIST;
    if (!showCompleted) list = list.filter(c => c.status !== 'completed');
    if (gradeFilter !== 'all') list = list.filter(c => Array.isArray(c.grades) && c.grades.includes(gradeFilter));
    return list;
  }, [classesWithIST, showCompleted, gradeFilter]);

  // For list view: "Upcoming & Active" ALWAYS excludes completed (that's its meaning);
  // "Recently completed" is the separate bucket below. The Show-completed toggle
  // affects the CALENDAR grid, not the semantic split of the list view.
  const activeListClasses = useMemo(
    () => [...classesWithIST]
      .filter(c => c.status !== 'completed')
      .filter(c => gradeFilter === 'all' ? true : (Array.isArray(c.grades) && c.grades.includes(gradeFilter)))
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time)),
    [classesWithIST, gradeFilter]
  );

  // Derived pagination for the List view
  const listTotalPages = Math.max(1, Math.ceil(activeListClasses.length / listPageSize));
  const listSafePage = Math.min(listPage, listTotalPages);
  const pagedListClasses = useMemo(
    () => activeListClasses.slice((listSafePage - 1) * listPageSize, listSafePage * listPageSize),
    [activeListClasses, listSafePage, listPageSize]
  );

  // Group classes by day-of-month for the visible grid
  const gridCells = useMemo(
    () => buildMonthGrid(monthCursor.year, monthCursor.month),
    [monthCursor]
  );

  const classesByCell = useMemo(() => {
    const map = new Map(); // key = ISO date "YYYY-MM-DD" of cell
    for (const cell of gridCells) {
      const key = `${cell.getFullYear()}-${cell.getMonth()}-${cell.getDate()}`;
      map.set(key, []);
    }
    for (const c of visibleClasses) {
      if (!c._ist) continue;
      const key = `${c._ist.year}-${c._ist.month}-${c._ist.date}`;
      if (map.has(key)) map.get(key).push(c);
    }
    // Sort each cell's list by start time
    for (const list of map.values()) list.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
    return map;
  }, [gridCells, visibleClasses]);

  const today = new Date();
  const monthLabel = `${MONTHS[monthCursor.month]} ${monthCursor.year}`;

  const prevMonth = () => setMonthCursor(mc => {
    const d = new Date(mc.year, mc.month - 1, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const nextMonth = () => setMonthCursor(mc => {
    const d = new Date(mc.year, mc.month + 1, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const goToday = () => {
    const n = new Date();
    setMonthCursor({ year: n.getFullYear(), month: n.getMonth() });
  };

  return (
    <>
      <Card data-testid="live-classes-calendar-card">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5" />
                Calendar
              </CardTitle>
              <CardDescription className="mt-1">
                {isFullAdmin
                  ? 'Overview of all scheduled classes. Click a class to see details.'
                  : 'Your assigned classes and the wider schedule. Click one to start when the window opens.'}
              </CardDescription>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Host filter — full admins only; sub-admins are scope-locked server-side */}
              {isFullAdmin && (
                <Tabs value={hostFilter} onValueChange={setHostFilter}>
                  <TabsList className="h-9" data-testid="calendar-host-filter">
                    <TabsTrigger value="all" className="text-xs" data-testid="calendar-host-all">
                      <Users className="w-3.5 h-3.5 mr-1" />All Hosts
                    </TabsTrigger>
                    <TabsTrigger value="mine" className="text-xs" data-testid="calendar-host-mine">
                      Only Me
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              )}

              {/* Grade filter */}
              <Select value={gradeFilter} onValueChange={setGradeFilter}>
                <SelectTrigger className="h-9 w-40" data-testid="calendar-grade-filter">
                  <SelectValue placeholder="Grade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All grades</SelectItem>
                  {GRADE_OPTIONS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>

              {/* View toggle */}
              <Tabs value={viewMode} onValueChange={setViewMode}>
                <TabsList className="h-9" data-testid="calendar-view-toggle">
                  <TabsTrigger value="calendar" className="text-xs" data-testid="calendar-view-cal">
                    <CalendarDays className="w-3.5 h-3.5 mr-1" />Calendar
                  </TabsTrigger>
                  <TabsTrigger value="list" className="text-xs" data-testid="calendar-view-list">
                    <List className="w-3.5 h-3.5 mr-1" />List
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {/* Show completed toggle */}
              <div className="flex items-center gap-2 pl-2 border-l" data-testid="calendar-completed-toggle-wrap">
                <Switch
                  id="show-completed"
                  checked={showCompleted}
                  onCheckedChange={setShowCompleted}
                  data-testid="calendar-show-completed-switch"
                />
                <Label htmlFor="show-completed" className="text-xs whitespace-nowrap cursor-pointer">
                  Show completed
                </Label>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Month nav — only shown in calendar mode */}
          {viewMode === 'calendar' && (
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={prevMonth} data-testid="calendar-prev-month">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <h3 className="font-semibold text-lg min-w-[180px] text-center" data-testid="calendar-month-label">
                  {monthLabel}
                </h3>
                <Button size="sm" variant="outline" onClick={nextMonth} data-testid="calendar-next-month">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              <Button size="sm" variant="ghost" onClick={goToday} data-testid="calendar-today-btn">
                Today
              </Button>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : viewMode === 'calendar' ? (
            <>
              {/* Day-of-week header */}
              <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-t-md overflow-hidden">
                {DAYS.map(d => (
                  <div key={d} className="bg-gray-50 text-xs font-semibold text-gray-600 text-center py-2">{d}</div>
                ))}
              </div>
              {/* 6-row grid */}
              <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-b-md overflow-hidden" data-testid="calendar-grid">
                {gridCells.map((cell, idx) => {
                  const isOtherMonth = cell.getMonth() !== monthCursor.month;
                  const isToday = isSameDay(cell, today);
                  const key = `${cell.getFullYear()}-${cell.getMonth()}-${cell.getDate()}`;
                  const dayClasses = classesByCell.get(key) || [];
                  const shownClasses = dayClasses.slice(0, 3);
                  const overflow = dayClasses.length - shownClasses.length;
                  return (
                    <div key={idx}
                      className={`bg-white min-h-[110px] p-1.5 relative ${isOtherMonth ? 'bg-gray-50' : ''}`}
                      data-testid={`calendar-cell-${key}`}>
                      <div className="flex items-center justify-between">
                        <span className={`inline-flex items-center justify-center text-xs font-semibold w-6 h-6 rounded-full ${
                          isToday ? 'bg-[#0A7EA4] text-white' :
                          isOtherMonth ? 'text-gray-400' : 'text-gray-700'
                        }`}>
                          {cell.getDate()}
                        </span>
                      </div>
                      <div className="mt-1 space-y-1">
                        {shownClasses.map(c => (
                          <button
                            key={c.id}
                            onClick={() => setDrawerClass(c)}
                            className={`w-full text-left text-[10px] leading-tight px-1.5 py-1 rounded truncate transition-colors ${chipStyle(c.status)}`}
                            title={`${c._ist ? `${String(c._ist.hour).padStart(2,'0')}:${String(c._ist.minute).padStart(2,'0')}` : ''} — ${c.title}`}
                            data-testid={`calendar-chip-${c.id}`}
                          >
                            {c.status === 'live' && <Radio className="inline w-2.5 h-2.5 mr-0.5" />}
                            <span className="font-semibold">{c._ist ? `${String(c._ist.hour).padStart(2,'0')}:${String(c._ist.minute).padStart(2,'0')}` : ''}</span>
                            {' '}{c.title}
                          </button>
                        ))}
                        {overflow > 0 && (
                          <button
                            onClick={() => setDrawerClass(dayClasses[shownClasses.length])}
                            className="text-[10px] text-gray-500 hover:text-gray-700 font-medium pl-1.5"
                            data-testid={`calendar-overflow-${key}`}
                          >
                            +{overflow} more
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 mt-4 text-xs text-gray-600 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 bg-blue-100 border border-blue-300 rounded"></span> Scheduled
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 bg-red-500 rounded animate-pulse"></span> Live now
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 bg-gray-200 rounded"></span> Completed
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 bg-red-50 border border-red-200 rounded"></span> Cancelled
                </div>
              </div>
            </>
          ) : (
            // LIST VIEW — active on top, "Recently completed" section below (always visible when >0)
            (activeListClasses.length === 0) ? (
              <div className="text-center py-12 text-gray-500" data-testid="calendar-list-empty">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No classes {hostFilter === 'mine' ? 'assigned to you' : 'scheduled'}.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Active section — completed classes now live in the dedicated tab */}
                {activeListClasses.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <Radio className="w-4 h-4 text-blue-600" />
                      Upcoming &amp; Active
                      <span className="text-xs text-gray-500 font-normal">({activeListClasses.length})</span>
                    </h4>
                    <Table data-testid="calendar-list-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Title</TableHead>
                          <TableHead>Start (IST)</TableHead>
                          <TableHead>Duration</TableHead>
                          <TableHead>Host</TableHead>
                          <TableHead>Grade</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pagedListClasses.map(c => (
                          <TableRow key={c.id}
                            onClick={() => setDrawerClass(c)}
                            className="cursor-pointer hover:bg-gray-50"
                            data-testid={`calendar-list-row-${c.id}`}>
                            <TableCell className="font-medium">{c.title}</TableCell>
                            <TableCell className="text-sm">{fmtISTLong(c.start_time)}</TableCell>
                            <TableCell className="text-sm">{c.duration_minutes} min</TableCell>
                            <TableCell className="text-sm">{c.host_user_name}</TableCell>
                            <TableCell className="text-xs">
                              {Array.isArray(c.grades) && c.grades.length > 0
                                ? c.grades.join(', ')
                                : <span className="text-gray-400">—</span>}
                            </TableCell>
                            <TableCell>
                              <Badge className={chipStyle(c.status).split(' ').filter(x => !x.startsWith('hover:')).join(' ')}>
                                {c.status === 'live' && <Radio className="inline w-3 h-3 mr-1" />}
                                {c.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-4 pt-3 border-t flex-wrap gap-3">
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <span>Rows per page</span>
                        <Select
                          value={String(listPageSize)}
                          onValueChange={(v) => { setListPageSize(parseInt(v)); setListPage(1); }}
                        >
                          <SelectTrigger className="h-8 w-20" data-testid="calendar-list-page-size">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PAGE_SIZE_OPTIONS.map(n => (
                              <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span className="ml-3">
                          Showing <strong>{activeListClasses.length === 0 ? 0 : (listSafePage - 1) * listPageSize + 1}</strong>
                          &ndash;<strong>{Math.min(listSafePage * listPageSize, activeListClasses.length)}</strong>
                          {' '}of <strong>{activeListClasses.length}</strong>
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm" variant="outline"
                          onClick={() => setListPage(p => Math.max(1, p - 1))}
                          disabled={listSafePage <= 1}
                          data-testid="calendar-list-prev-page"
                        >
                          <ChevronLeft className="w-4 h-4 mr-1" />Prev
                        </Button>
                        <span className="text-xs px-3 tabular-nums whitespace-nowrap" data-testid="calendar-list-page-indicator">
                          Page {listSafePage} of {listTotalPages}
                        </span>
                        <Button
                          size="sm" variant="outline"
                          onClick={() => setListPage(p => Math.min(listTotalPages, p + 1))}
                          disabled={listSafePage >= listTotalPages}
                          data-testid="calendar-list-next-page"
                        >
                          Next<ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* "Recently completed" removed — dedicated Completed Classes tab now hosts these. */}
              </div>
            )
          )}
        </CardContent>
      </Card>

      <LiveClassDetailsDrawer
        open={!!drawerClass}
        onOpenChange={(v) => { if (!v) setDrawerClass(null); }}
        cls={drawerClass}
      />
    </>
  );
};

export default LiveClassesCalendar;
